import React, { useEffect, useState } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Toast, { type ToastMessage } from '../common/Toast';
import {
    fetchIncidentTypesDropdown, fetchCategoriesDropdown, fetchRisksDropdown,
    fetchProcessesDropdown, fetchDepartmentsDropdown, incidentsApi,
} from '../../services/incidentService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { maxLenForCode } from '../../validation/engine';

type DDItem = { id: number; name: string; code?: string };

interface CreateIncidentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const OCCURRENCE_TYPE_OPTIONS = [
    { value: 'Actual Event', label: 'Actual Event' },
    { value: 'Near Miss', label: 'Near Miss' },
    { value: 'Reported Observation', label: 'Reported Observation' },
];

const CreateIncidentModal: React.FC<CreateIncidentModalProps> = ({ isOpen, onClose, onCreated }) => {
    const [types, setTypes] = useState<DDItem[]>([]);
    const [categories, setCategories] = useState<DDItem[]>([]);
    const [risks, setRisks] = useState<DDItem[]>([]);
    const [processes, setProcesses] = useState<DDItem[]>([]);
    const [departments, setDepartments] = useState<DDItem[]>([]);

    const BLANK_FORM = {
        incident_master_id: '' as number | '',
        incident_category_id: '' as number | '',
        risk_id: '' as number | '',
        process_id: '' as number | '',
        department_id: '' as number | '',
        occurrence_type: 'Actual Event',
        description: '',
    };
    const [form, setForm] = useState(BLANK_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { errors, validateField, clearError, validateAll, reset } = useFieldValidation();

    // Reset the whole form (not just validation errors) each time the modal
    // opens, so a prior successful submit doesn't leave stale values pre-filled.
    useEffect(() => {
        if (isOpen) { setForm(BLANK_FORM); setToast(null); }
        reset();
    }, [isOpen, reset]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        Promise.all([
            fetchIncidentTypesDropdown().then(r => r.items).catch(() => []),
            fetchCategoriesDropdown().then(r => r.items).catch(() => []),
            fetchRisksDropdown().then(r => r.items).catch(() => []),
            fetchProcessesDropdown().then(r => r.items).catch(() => []),
            fetchDepartmentsDropdown().then(r => r.items).catch(() => []),
        ]).then(([t, c, r, p, d]) => {
            setTypes(t); setCategories(c); setRisks(r); setProcesses(p); setDepartments(d);
        });
    }, []);

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const ok = validateAll([
            { name: 'description', code: 110, value: form.description, required: true },
        ]);
        if (!ok) return;
        setSubmitting(true); setToast(null);
        try {
            await incidentsApi.create({
                incident_description: form.description,
                occurrence_type: form.occurrence_type,
                incident_master_id: form.incident_master_id ? Number(form.incident_master_id) : undefined,
                incident_category_id: form.incident_category_id ? Number(form.incident_category_id) : undefined,
                risk_id: form.risk_id ? Number(form.risk_id) : undefined,
                process_id: form.process_id ? Number(form.process_id) : undefined,
                department_id: form.department_id ? Number(form.department_id) : undefined,
            });
            onCreated();
        } catch (e: any) {
            setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to create' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                <span className="flex items-center gap-2">
                    <AlertCircle className="text-blue-600" size={20} />
                    Create Ad-hoc Incident
                </span>
            </Modal.Header>
            <Modal.Body>
                <form id="create-incident-form" onSubmit={handleSubmit} className="flex flex-col gap-y-4" data-testid="MOD_create_incident_form">
                    <p className="text-sm text-gray-500">Manually log an incident for triage.</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FormControl.SearchSelect
                            label="Incident Type"
                            options={types.map(t => ({ value: t.id, label: t.name }))}
                            value={form.incident_master_id || null}
                            onChange={v => setForm({ ...form, incident_master_id: v ? Number(v) : '' })}
                            placeholder="Select type…"
                            clearable
                            data-testid="SEL_create_incident_type"
                        />
                        <FormControl.SearchSelect
                            label="Category"
                            options={categories.map(c => ({ value: c.id, label: c.name }))}
                            value={form.incident_category_id || null}
                            onChange={v => setForm({ ...form, incident_category_id: v ? Number(v) : '' })}
                            placeholder="Select category…"
                            clearable
                            data-testid="SEL_create_incident_category"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FormControl.SearchSelect
                            label="Risk"
                            options={risks.map(r => ({ value: r.id, label: r.name }))}
                            value={form.risk_id || null}
                            onChange={v => setForm({ ...form, risk_id: v ? Number(v) : '' })}
                            placeholder="— None (optional) —"
                            clearable
                            data-testid="SEL_create_incident_risk"
                        />
                        <FormControl.SearchSelect
                            label="Process"
                            options={processes.map(p => ({ value: p.id, label: p.name }))}
                            value={form.process_id || null}
                            onChange={v => setForm({ ...form, process_id: v ? Number(v) : '' })}
                            placeholder="— None (optional) —"
                            clearable
                            data-testid="SEL_create_incident_process"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FormControl.SearchSelect
                            label="Occurrence Type"
                            options={OCCURRENCE_TYPE_OPTIONS}
                            value={form.occurrence_type}
                            onChange={v => setForm({ ...form, occurrence_type: String(v ?? 'Actual Event') })}
                            data-testid="SEL_create_incident_occurrence_type"
                        />
                        <FormControl.SearchSelect
                            label="Department"
                            options={departments.map(d => ({ value: d.id, label: d.name }))}
                            value={form.department_id || null}
                            onChange={v => setForm({ ...form, department_id: v ? Number(v) : '' })}
                            placeholder="— None (optional) —"
                            clearable
                            data-testid="SEL_create_incident_department"
                        />
                    </div>
                    <FormControl.Textarea
                        label="Description"
                        required
                        rows={4}
                        placeholder="Describe the incident details..."
                        value={form.description}
                        onChange={e => { setForm({ ...form, description: e.target.value }); clearError('description'); }}
                        onBlur={() => validateField('description', 110, form.description, { required: true })}
                        maxLength={maxLenForCode(110)}
                        error={errors.description}
                        data-testid="INP_create_incident_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    type="submit"
                    form="create-incident-form"
                    variant="fill"
                    icon={Save}
                    disabled={submitting}
                    data-testid="BTN_create_incident_submit"
                >
                    {submitting ? 'Submitting...' : 'Submit'}
                </Button>
            </Modal.Footer>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

export default CreateIncidentModal;
